import { calendarNotificationsBarUpdateEventFailed } from 'owa-locstrings/lib/strings/calendarnotificationsbarupdateeventfailed.locstring.json';
import { calendarNotificationsBarCreateEventFailed } from 'owa-locstrings/lib/strings/calendarnotificationsbarcreateeventfailed.locstring.json';
import { calendarNotificationsBarDeleteEventFailed } from 'owa-locstrings/lib/strings/calendarnotificationsbardeleteeventfailed.locstring.json';
import loc from 'owa-localize';
import { CalendarEventsLoadingState } from '../types/CalendarEventsLoadingState';
import { ActionDatapoint } from 'owa-analytics';
import { addDatapointConfig } from 'owa-analytics-actions';
import { ViewType } from 'owa-calendar-actions';
import { CalendarId } from 'owa-calendar-cache';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { ClientItemId } from 'owa-client-ids';
import BaseItemId from 'owa-service/lib/contract/BaseItemId';
import EventScope from 'owa-service/lib/contract/EventScope';
import { CalendarEventEntity } from '../store/schema/CalendarEventEntity';

import { action } from 'satcheljs';

export const calendarEventsRemoved = action(
    'calendarEventsRemoved',
    (eventsRemoved: CalendarEvent[]) => ({
        eventsRemoved,
    })
);

export const calendarEventsAdded = action(
    'calendarEventsAdded',
    (eventsAdded: CalendarEvent[]) => ({
        eventsAdded,
    })
);

export const calendarEventUpdated = action(
    'calendarEventUpdated',
    (eventUpdated: CalendarEvent) => ({
        eventUpdated,
    })
);

export const cacheReloaded = action(
    'cacheReloaded',
    (previousEvents: CalendarEvent[], reloadedEvents: CalendarEvent[]) => ({
        previousEvents,
        reloadedEvents,
    })
);

export const deleteEventsCache = action('deleteEventsCache', (folderId: string) => ({ folderId }));

export const loadingCalendarEvents = action(
    'loadingCalendarEvents',
    (calendarId: CalendarId, loadingState: CalendarEventsLoadingState) =>
        addDatapointConfig(
            {
                name: 'CalendarLoadState',
                customData: {
                    loadingState,
                },
                options: {
                    isVerbose: true,
                },
            },
            {
                calendarId,
                loadingState,
            }
        )
);

/**
 * Calendar item operation result
 */
export interface CalendarEventOperationResult {
    isSuccessful: boolean;
}

/**
 * Create operation result
 */
export interface CalendarEventCreateResult extends CalendarEventOperationResult {
    createdItem: CalendarEvent;
}

/**
 * It creates a calendar event locally (local lie) while also sending a request to create
 * the event.
 * @param eventToCreate The event that should be created.
 * @param actionToExecute The action that will be invoked to create calendar event at backend.
 * @param onEventCreatedSuccess Will be called when the event is created successfully at the backend using `actionToExecute`. The created event will be passed to it as param.
 * @param onEventCreatedFailed Will be called if there is an error in creating calendar event.
 * @param actionSource the ViewType from which this action was triggered
 * @param errorMessage The error message to show in case of failure.
 * @param onEventCreatedLocally callback which is triggered when the event is created locally
 */
export const instantCalendarEventCreate = action(
    'instantCalendarEventCreate',
    <T extends CalendarEventCreateResult>(
        eventToCreate: CalendarEvent,
        actionToExecute: (eventToCreate: CalendarEvent) => Promise<T>,
        onEventCreatedSuccess: (instantEvent: CalendarEvent) => void,
        onEventCreatedFailed: (
            error: any,
            retryFunction: () => void | null,
            actionSource: ViewType
        ) => void = () => {},
        actionSource: ViewType = undefined,
        errorMessage: string = loc(calendarNotificationsBarCreateEventFailed),
        onEventCreatedLocally: (actionSource: ViewType) => void = () => {}
    ) => ({
        eventToCreate,
        actionToExecute,
        onEventCreatedSuccess,
        onEventCreatedFailed,
        actionSource,
        errorMessage,
        onEventCreatedLocally,
    })
);

/**
 * It updates the calendar event locally (local lie) while also sending a request to update
 * the event.
 * @param eventId The id of the event to update.
 * @param eventChanges The changes in the event that need to be updated.
 * @param actionToExecute The action that will be invoked to update calendar event at backend.
 * @param onEventUpdateSuccess Will be called when the event is updated successfully at the backend using `actionToExecute`.
 * @param onEventUpdateFailed Will be called if there is an error in updating calendar event.
 * @param actionSource the ViewType from which this action was triggered
 * @param errorMessage The error message to show in case of failure.
 * @param onEventUpdatedLocally called when an event has been upated locally
 */
export const instantCalendarEventUpdate = action(
    'instantCalendarEventUpdate',
    <T extends CalendarEventOperationResult>(
        eventId: ClientItemId,
        eventChanges: CalendarEvent,
        eventScope: EventScope,
        actionToExecute: (idToUpdate: BaseItemId) => Promise<T>,
        onEventUpdateSuccess: () => void,
        onEventUpdateFailed: (
            error: any,
            retryFunction: () => void | null,
            actionSource: ViewType
        ) => void = () => {},
        actionSource: ViewType = undefined,
        errorMessage: string = loc(calendarNotificationsBarUpdateEventFailed),
        onEventUpdatedLocally: (event: CalendarEvent, actionSource: ViewType) => void = () => {}
    ) => ({
        eventId,
        eventChanges,
        eventScope,
        actionToExecute,
        onEventUpdateSuccess,
        onEventUpdateFailed,
        actionSource,
        errorMessage,
        onEventUpdatedLocally,
    })
);

/**
 * It deletes the calendar event locally (local lie) while also sending a request to delete
 * the event.
 * @param eventId The id of the event to delete.
 * @param eventToDelete The event that should be deleted.
 * @param dataPoint passed from previous action so that user perceived time can be marked
 * @param actionToExecute The action that will be invoked to delete calendar event at backend.
 * @param onEventDeleteSuccess Will be called when the event is deleted successfully at the backend using `actionToExecute`.
 * @param onEventDeleteFailed Will be called if there is an error in deleting calendar event.
 * @param actionSource the ViewType from which this action was triggered
 * @param errorMessage The error message to show in case of failure.
 * @param responseSent Whether or not the user chose to send a response to the organizer
 * @param asyncUndoAction an undo action that will be awaited before sending the server request to delete the event, if true is returned, the
 * delete is undon locally and the server request to delete is not sent
 */
export const instantCalendarEventDelete = action(
    'instantCalendarEventDelete',
    <T extends CalendarEventOperationResult>(
        eventId: ClientItemId,
        eventScope: EventScope,
        dataPoint: ActionDatapoint,
        actionToExecute: (id: BaseItemId) => Promise<T>,
        onEventDeleteSuccess: () => void,
        onEventDeleteFailed: (
            error: any,
            retryFunction: () => void | null,
            actionSource: ViewType
        ) => void = () => {},
        actionSource: ViewType = undefined,
        errorMessage: string = loc(calendarNotificationsBarDeleteEventFailed),
        responseSent: boolean = false,
        asyncUndoAction: (
            eventToDelete: CalendarEventEntity,
            responseSent: boolean,
            actionSource: ViewType
        ) => Promise<boolean> = () => Promise.resolve(false)
    ) => ({
        eventId,
        eventScope,
        dataPoint,
        actionToExecute,
        onEventDeleteSuccess,
        onEventDeleteFailed,
        actionSource,
        errorMessage,
        responseSent,
        asyncUndoAction,
    })
);
